import { useState, useMemo } from "react";
import { View, Text, Pressable, ScrollView } from "react-native";
import {
  Snowflake,
  Baby,
  Heart,
  Telescope,
  ChevronRight,
  ChevronLeft,
  Compass,
  Gem,
  HeartHandshake,
  HelpCircle,
  CalendarClock,
  Check,
  Activity,
  Egg,
  Droplets,
} from "lucide-react-native";
import { EMBER } from "../../../../onboarding/theme";
import { usePatient } from "@louise/patients/hooks/usePatient";
import { Dialog } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type { TranslationFunction } from "@src/packages/i18n";

export type IntentPickerHomepageStage =
  "explore" | "before-treatment" | "in-care";

type TreatmentGoalValue =
  | "exploring"
  | "egg-preserve"
  | "sperm-preserve"
  | "embryo-preserve"
  | "pregnancy"
  | "egg-donation"
  | "sperm-donation"
  | "surrogacy"
  | "adoption";

type Goal = "preserve" | "grow" | "donate" | "explore";

type GoalItem = {
  id: Goal;
  label: string;
  sublabel: string;
  Icon: typeof Snowflake;
  directGoal?: TreatmentGoalValue;
  homepageStage?: IntentPickerHomepageStage;
};

function getGoals(t: TranslationFunction): GoalItem[] {
  return [
    {
      id: "preserve",
      label: t("you.intent_picker.goal.preserve_label"),
      sublabel: t("you.intent_picker.goal.preserve_sublabel"),
      Icon: Snowflake,
    },
    {
      id: "grow",
      label: t("you.intent_picker.goal.grow_label"),
      sublabel: t("you.intent_picker.goal.grow_sublabel"),
      Icon: Baby,
    },
    {
      id: "donate",
      label: t("you.intent_picker.goal.donate_label"),
      sublabel: t("you.intent_picker.goal.donate_sublabel"),
      Icon: Heart,
    },
    {
      id: "explore",
      label: t("you.intent_picker.goal.explore_label"),
      sublabel: t("you.intent_picker.goal.explore_sublabel"),
      Icon: Telescope,
      directGoal: "exploring",
      homepageStage: "explore",
    },
  ];
}

type StageOption = {
  label: string;
  sublabel?: string;
  goal: TreatmentGoalValue;
  homepageStage: IntentPickerHomepageStage;
  Icon: typeof Compass;
};

function getPreserveOptions(
  t: TranslationFunction,
  gender: string | null | undefined,
): StageOption[] {
  const isAMAB = gender === "AMAB";
  return [
    {
      label: t("you.intent_picker.preserve.exploring_label"),
      sublabel: t("you.intent_picker.preserve.exploring_sublabel"),
      goal: "exploring",
      homepageStage: "explore",
      Icon: Compass,
    },
    {
      label: isAMAB
        ? t("you.intent_picker.preserve.sperm_freezing_label")
        : t("you.intent_picker.preserve.egg_freezing_label"),
      sublabel: t("you.intent_picker.preserve.freezing_sublabel"),
      goal: isAMAB ? "sperm-preserve" : "egg-preserve",
      homepageStage: "before-treatment",
      Icon: Snowflake,
    },
    {
      label: t("you.intent_picker.preserve.embryo_label"),
      sublabel: t("you.intent_picker.preserve.embryo_sublabel"),
      goal: "embryo-preserve",
      homepageStage: "before-treatment",
      Icon: Gem,
    },
  ];
}

function getGrowOptions(t: TranslationFunction): StageOption[] {
  return [
    {
      label: t("you.intent_picker.grow.trying_label"),
      sublabel: t("you.intent_picker.grow.trying_sublabel"),
      goal: "pregnancy",
      homepageStage: "explore",
      Icon: HeartHandshake,
    },
    {
      label: t("you.intent_picker.grow.researching_label"),
      sublabel: t("you.intent_picker.grow.researching_sublabel"),
      goal: "exploring",
      homepageStage: "explore",
      Icon: HelpCircle,
    },
    {
      label: t("you.intent_picker.grow.adoption_label"),
      sublabel: t("you.intent_picker.grow.adoption_sublabel"),
      goal: "adoption",
      homepageStage: "explore",
      Icon: HeartHandshake,
    },
    {
      label: t("you.intent_picker.grow.ready_label"),
      sublabel: t("you.intent_picker.grow.ready_sublabel"),
      goal: "pregnancy",
      homepageStage: "before-treatment",
      Icon: CalendarClock,
    },
    {
      label: t("you.intent_picker.grow.in_treatment_label"),
      sublabel: t("you.intent_picker.grow.in_treatment_sublabel"),
      goal: "pregnancy",
      homepageStage: "in-care",
      Icon: Activity,
    },
  ];
}

function getHelpOptions(
  t: TranslationFunction,
  gender: string | null | undefined,
): StageOption[] {
  const isAMAB = gender === "AMAB";
  if (isAMAB) {
    return [
      {
        label: t("you.intent_picker.help.sperm_donation_label"),
        sublabel: t("you.intent_picker.help.sperm_donation_sublabel"),
        goal: "sperm-donation",
        homepageStage: "explore",
        Icon: Droplets,
      },
    ];
  }
  return [
    {
      label: t("you.intent_picker.help.egg_donation_label"),
      sublabel: t("you.intent_picker.help.egg_donation_sublabel"),
      goal: "egg-donation",
      homepageStage: "explore",
      Icon: Egg,
    },
    {
      label: t("you.intent_picker.help.surrogacy_label"),
      sublabel: t("you.intent_picker.help.surrogacy_sublabel"),
      goal: "surrogacy",
      homepageStage: "before-treatment",
      Icon: HeartHandshake,
    },
  ];
}

type Props = {
  visible: boolean;
  currentIntent: string | null;
  genderOverride?: "AFAB" | "AMAB" | null;
  onSelect: (
    goal: string,
    homepageStage?: IntentPickerHomepageStage,
  ) => void | boolean | Promise<void | boolean>;
  onClose: () => void;
  isSaving?: boolean;
  saveError?: string | null;
};

function PickerRow({
  Icon,
  label,
  sublabel,
  selected,
  trailing,
  disabled = false,
  onPress,
}: {
  Icon: typeof Snowflake;
  label: string;
  sublabel?: string;
  selected: boolean;
  trailing: "chevron" | "check" | "none";
  disabled?: boolean;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("PickerRow");
  const innerContent = (
    <>
      <Icon
        size={20}
        color={selected ? EMBER.colors.warm : EMBER.colors.textMid}
        strokeWidth={1.5}
      />
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            fontWeight: selected ? "600" : "500",
            color: EMBER.colors.text,
          }}
        >
          {label}
        </Text>
        {sublabel && (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textLight,
              marginTop: 1,
            }}
          >
            {sublabel}
          </Text>
        )}
      </View>
      {trailing === "check" && (
        <Check size={18} color={EMBER.colors.warm} strokeWidth={2.5} />
      )}
      {trailing === "chevron" && (
        <ChevronRight
          size={16}
          color={EMBER.colors.textLight}
          strokeWidth={1.5}
        />
      )}
    </>
  );

  return (
    <Pressable
      disabled={disabled}
      onPress={() => {
        capturePress(pressLabel(label), "intent_picker_row");
        onPress();
      }}
      style={({ pressed }) => ({
        flexDirection: "row",
        alignItems: "center",
        gap: 14,
        paddingVertical: 14,
        paddingHorizontal: 14,
        backgroundColor: selected
          ? "rgba(184, 74, 22, 0.06)"
          : pressed
            ? "rgba(0, 0, 0, 0.04)"
            : "rgba(255, 255, 255, 0.5)",
        borderRadius: 12,
        borderWidth: 1,
        borderColor: selected ? EMBER.colors.warm : "rgba(0, 0, 0, 0.06)",
        opacity: disabled ? 0.55 : 1,
      })}
    >
      {innerContent}
    </Pressable>
  );
}

export default function IntentPickerModal({
  visible,
  currentIntent,
  genderOverride = null,
  onSelect,
  onClose,
  isSaving = false,
  saveError = null,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("IntentPickerModal");
  const { data: patient } = usePatient();
  const [step, setStep] = useState<"goal" | "stage">("goal");
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);

  const goals = useMemo(() => getGoals(t), [t]);
  const pickerGender = genderOverride ?? patient?.gender;
  const preserveOptions = useMemo(
    () => getPreserveOptions(t, pickerGender),
    [pickerGender, t],
  );
  const growOptions = useMemo(() => getGrowOptions(t), [t]);
  const helpOptions = useMemo(
    () => getHelpOptions(t, pickerGender),
    [pickerGender, t],
  );
  const donationGoal =
    helpOptions.length === 1 ? helpOptions[0]?.goal : undefined;

  const resetAfterAnimation = () => {
    setTimeout(() => {
      setStep("goal");
      setSelectedGoal(null);
    }, 300);
  };

  const handleClose = () => {
    onClose();
    resetAfterAnimation();
  };

  const handleGoalSelect = async (goal: Goal) => {
    const goalDef = goals.find((g) => g.id === goal);
    if (goalDef?.directGoal) {
      const saved = await onSelect(goalDef.directGoal, goalDef.homepageStage);
      if (saved !== false) resetAfterAnimation();
      return;
    }
    // If the sub-step would only have one option, skip step 2.
    if (goal === "donate" && donationGoal) {
      const option = helpOptions.find((opt) => opt.goal === donationGoal);
      const saved = await onSelect(donationGoal, option?.homepageStage);
      if (saved !== false) resetAfterAnimation();
      return;
    }
    setSelectedGoal(goal);
    setStep("stage");
  };

  const handleOptionSelect = async (goal: TreatmentGoalValue) => {
    const option = options.find((opt) => opt.goal === goal);
    const saved = await onSelect(goal, option?.homepageStage);
    if (saved !== false) resetAfterAnimation();
  };

  const handleBack = () => {
    setStep("goal");
    setSelectedGoal(null);
  };

  const options =
    selectedGoal === "preserve"
      ? preserveOptions
      : selectedGoal === "donate"
        ? helpOptions
        : growOptions;
  const optionTitle = t("you.intent_picker.title_stage");

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      layout="centered"
      closeButton="minimal"
    >
      {step === "stage" && (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("you.shared.back")}
          onPress={() => {
            capturePress(pressLabel(t("you.shared.back")), "you.shared.back");
            handleBack();
          }}
          hitSlop={12}
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            width: 28,
            height: 28,
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
        >
          <ChevronLeft
            size={20}
            color={EMBER.colors.textLight}
            strokeWidth={2}
          />
        </Pressable>
      )}

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 20,
          fontWeight: "700",
          color: EMBER.colors.text,
          textAlign: "center",
          marginBottom: step === "goal" ? 8 : 20,
        }}
      >
        {step === "goal" ? t("you.intent_picker.title_goal") : optionTitle}
      </Text>
      {step === "goal" && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: EMBER.colors.textLight,
            textAlign: "center",
            lineHeight: 20,
            marginBottom: 20,
          }}
        >
          {t("you.intent_picker.subtitle")}
        </Text>
      )}

      {isSaving && (
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textLight,
            fontSize: 13,
            marginBottom: 12,
            textAlign: "center",
          }}
        >
          {t("you.intent_picker.journey_save_in_progress")}
        </Text>
      )}
      {saveError && (
        <Text
          className="font-dm-sans"
          accessibilityRole="alert"
          style={{
            color: EMBER.colors.coral,
            fontSize: 13,
            lineHeight: 18,
            marginBottom: 12,
            textAlign: "center",
          }}
        >
          {saveError}
        </Text>
      )}

      <ScrollView
        testID="intent-picker-options"
        showsVerticalScrollIndicator={false}
        style={{ maxHeight: 420 }}
        contentContainerStyle={{ gap: 8, paddingBottom: 4 }}
      >
        {step === "goal"
          ? goals.map((goal) => {
              const directGoal =
                goal.id === "donate" && helpOptions.length === 1
                  ? helpOptions[0]?.goal
                  : goal.directGoal;
              const isDirectAndSelected =
                directGoal !== undefined && currentIntent === directGoal;
              return (
                <PickerRow
                  key={goal.id}
                  Icon={goal.Icon}
                  label={goal.label}
                  sublabel={goal.sublabel}
                  selected={isDirectAndSelected}
                  trailing={
                    directGoal !== undefined
                      ? isDirectAndSelected
                        ? "check"
                        : "none"
                      : "chevron"
                  }
                  disabled={isSaving}
                  onPress={() => void handleGoalSelect(goal.id)}
                />
              );
            })
          : options.map((opt, i) => {
              const isSelected = currentIntent === opt.goal;
              return (
                <PickerRow
                  key={`${opt.goal}-${i}`}
                  Icon={opt.Icon}
                  label={opt.label}
                  sublabel={opt.sublabel}
                  selected={isSelected}
                  trailing={isSelected ? "check" : "none"}
                  disabled={isSaving}
                  onPress={() => void handleOptionSelect(opt.goal)}
                />
              );
            })}
      </ScrollView>
    </Dialog>
  );
}
